// Custom shape compiler: folds the editor's layers (primitives and imported
// outlines with union/subtract roles) into normalized outline polygons via
// polygon-clipping. The result is centered on the bbox middle with its
// longest dimension scaled to 1; placement multiplies by the size field, so
// the same compiled shape serves preview and every exporter.

import pc from 'polygon-clipping'
import type { CustomShape, Poly, Ring, ShapeLayer, Vec2 } from './types'
import {
  circleRing,
  circleSegments,
  ensureWinding,
  ringBBox,
  roundCorners,
  signedArea,
} from './geometry'

function layerRing(layer: ShapeLayer, tol: number): Ring | null {
  const w = Math.max(1e-3, layer.w)
  const h = Math.max(1e-3, layer.h)
  let ring: Ring
  switch (layer.kind) {
    case 'circle': {
      // Ellipse when w != h: scale a unit circle.
      const base = circleRing(0, 0, 0.5, tol / Math.max(w, h))
      ring = base.map(([x, y]) => [x * w, y * h])
      break
    }
    case 'rect':
      ring = [
        [-w / 2, -h / 2],
        [w / 2, -h / 2],
        [w / 2, h / 2],
        [-w / 2, h / 2],
      ]
      break
    case 'slot': {
      const rr = Math.min(w, h) / 2
      const half = w / 2 - rr
      const segs = Math.max(4, Math.floor(circleSegments(1, tol / rr) / 2))
      ring = []
      for (let i = 0; i <= segs; i++) {
        const a = -Math.PI / 2 + (i / segs) * Math.PI
        ring.push([half + rr * Math.cos(a), rr * Math.sin(a)])
      }
      for (let i = 0; i <= segs; i++) {
        const a = Math.PI / 2 + (i / segs) * Math.PI
        ring.push([-half + rr * Math.cos(a), rr * Math.sin(a)])
      }
      break
    }
    case 'polygon': {
      const sides = Math.max(3, Math.round(layer.sides))
      ring = []
      for (let i = 0; i < sides; i++) {
        const a = (i / sides) * Math.PI * 2 - Math.PI / 2
        ring.push([(Math.cos(a) * w) / 2, (Math.sin(a) * h) / 2])
      }
      break
    }
    case 'star': {
      const points = Math.max(3, Math.round(layer.starPoints))
      const inner = Math.max(0.1, Math.min(0.95, layer.starInner))
      ring = []
      for (let i = 0; i < points * 2; i++) {
        const rad = i % 2 === 0 ? 0.5 : 0.5 * inner
        const a = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2
        ring.push([Math.cos(a) * rad * w, Math.sin(a) * rad * h])
      }
      break
    }
    case 'outline': {
      if (layer.outline.length < 3) return null
      const raw: Ring = layer.outline.map((p: Vec2) => [p.x, p.y])
      const bb = ringBBox(raw)
      const sw = bb.maxX - bb.minX
      const sh = bb.maxY - bb.minY
      if (sw < 1e-9 || sh < 1e-9) return null
      const cx = (bb.minX + bb.maxX) / 2
      const cy = (bb.minY + bb.maxY) / 2
      const s = Math.min(w / sw, h / sh)
      ring = raw.map(([x, y]) => [(x - cx) * s, (y - cy) * s])
      break
    }
  }

  ring = ensureWinding(ring, true)
  if (layer.cornerRadius > 0 && layer.kind !== 'circle' && layer.kind !== 'slot') {
    ring = roundCorners(ring, layer.cornerRadius, tol)
  }
  if (layer.mirrorH) ring = ensureWinding(ring.map(([x, y]) => [-x, y] as [number, number]), true)
  if (layer.mirrorV) ring = ensureWinding(ring.map(([x, y]) => [x, -y] as [number, number]), true)
  if (layer.rotation !== 0) {
    const a = (layer.rotation * Math.PI) / 180
    const c = Math.cos(a)
    const s = Math.sin(a)
    ring = ring.map(([x, y]) => [x * c - y * s, x * s + y * c])
  }
  return ring.map(([x, y]) => [x + layer.x, y + layer.y])
}

function normalizeParts(parts: [number, number][][][]): Poly[] {
  const polys: Poly[] = []
  for (const poly of parts) {
    const rings: Ring[] = []
    for (let i = 0; i < poly.length; i++) {
      let r = poly[i] as Ring
      const first = r[0]
      const last = r[r.length - 1]
      if (r.length > 1 && first[0] === last[0] && first[1] === last[1]) r = r.slice(0, -1)
      if (r.length < 3 || Math.abs(signedArea(r)) < 1e-9) continue
      rings.push(ensureWinding(r, i === 0))
    }
    if (rings.length > 0) polys.push(rings)
  }
  return polys
}

// Compile at design scale, without normalization. Used by the editor preview.
export function compileLayers(cs: CustomShape, tol: number): Poly[] {
  let acc: [number, number][][][] = []
  for (const layer of cs.layers) {
    const ring = layerRing(layer, tol)
    if (!ring) continue
    const piece: [number, number][][] = [ring as [number, number][]]
    try {
      if (acc.length === 0) {
        if (layer.role === 'union') acc = [piece]
        // Subtracting from nothing stays nothing.
      } else if (layer.role === 'union') {
        acc = pc.union(acc, [piece])
      } else {
        acc = pc.difference(acc, [piece])
      }
    } catch {
      // A degenerate boolean never kills compilation; skip the layer.
    }
  }
  return normalizeParts(acc)
}

// Compile and normalize: bbox-centered, longest dimension 1. Returns [] when
// nothing solid remains (the pipeline then falls back to a circle).
export function compileCustomShape(cs: CustomShape, tol: number): Poly[] {
  const polys = compileLayers(cs, tol)
  if (polys.length === 0) return []
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const poly of polys) {
    const bb = ringBBox(poly[0])
    minX = Math.min(minX, bb.minX)
    minY = Math.min(minY, bb.minY)
    maxX = Math.max(maxX, bb.maxX)
    maxY = Math.max(maxY, bb.maxY)
  }
  const span = Math.max(maxX - minX, maxY - minY)
  if (span < 1e-9) return []
  const cx = (minX + maxX) / 2
  const cy = (minY + maxY) / 2
  return polys.map((poly) =>
    poly.map((ring) => ring.map(([x, y]) => [(x - cx) / span, (y - cy) / span] as [number, number])),
  )
}
